import { ButtonType, EpicIcons } from "@constellation-academy/epic-ui";
import * as Sentry from "@sentry/react";
import { memo, useState } from "react";
import { useFragment, useMutation } from "react-relay";
import { toast } from "react-toastify";
import { useTranslation } from "@i18n/hooks";
import type { createTreeStateButton_CreateTreeStateForUserMutation } from "@relay/createTreeStateButton_CreateTreeStateForUserMutation.graphql";
import type { createTreeStateButton_LearnOpportunityV2Fragment$key } from "@relay/createTreeStateButton_LearnOpportunityV2Fragment.graphql";
import {
	LEARN_OPPORTUNITY_V2_FRAGMENT,
	CREATE_TREE_STATE_FOR_USER_MUTATION,
} from "./create-tree-state-button.graphql";
import type { CreateTreeStateForUserInput } from "./create-tree-state-button.types";
import { Button } from "../../../../components/button";
import { ConfirmDialog } from "../../../../components/confirm-dialog";

const CreateTreeStateButtonComponent = ({
	learnOpportunityV2FragmentRef,
	userId,
	onCompleted,
	onError,
}: CreateTreeStateForUserInput) => {
	const { t } = useTranslation("common");
	const [showConfirmDialog, setShowConfirmDialog] = useState(false);

	const rootNode = useFragment<createTreeStateButton_LearnOpportunityV2Fragment$key>(
		LEARN_OPPORTUNITY_V2_FRAGMENT,
		learnOpportunityV2FragmentRef ?? null,
	);

	const [createTreeState, isCreatingTreeState] =
		useMutation<createTreeStateButton_CreateTreeStateForUserMutation>(
			CREATE_TREE_STATE_FOR_USER_MUTATION,
		);

	const handleCreateTreeStateOnClick = () => {
		setShowConfirmDialog(true);
	};

	const handleCreateTreeStateOnCancel = () => {
		setShowConfirmDialog(false);
	};

	const handleCreateTreeStateOnConfirm = () => {
		if (!rootNode) {
			setShowConfirmDialog(false);
			toast.error(t("userEdit.unlockCourseRootNotFound"));
			Sentry.captureException(
				new Error(
					"createTreeStateButton_CreateTreeStateForUserMutation: RootNode not found",
				),
				{
					extra: {
						userId,
						rootNode,
					},
				},
			);
			return;
		}

		createTreeState({
			variables: {
				input: { rootId: rootNode.id, userId },
			},
			onCompleted: () => {
				setShowConfirmDialog(false);
				toast.success(t("userEdit.unlockCourseSuccess"));
				onCompleted?.();
			},
			onError: () => {
				setShowConfirmDialog(false);
				toast.error(t("userEdit.unlockCourseError"));
				onError?.();
			},
		});
	};

	return (
		<>
			<Button
				type={ButtonType.Button}
				icon={EpicIcons.UNLOCK}
				label={t("userEdit.unlockCourse")}
				onClick={handleCreateTreeStateOnClick}
				disabled={isCreatingTreeState}
				loading={isCreatingTreeState}
			/>
			<ConfirmDialog
				title={t("userEdit.unlockCourse")}
				content={t("userEdit.unlockCourseConfirm", {
					name: rootNode?.structureDefinition.title ?? t("userEdit.unknownCourse"),
				})}
				confirmButtonText={t("userEdit.unlockCourseConfirmButton")}
				onConfirm={handleCreateTreeStateOnConfirm}
				onCancel={handleCreateTreeStateOnCancel}
				showDialog={showConfirmDialog}
			/>
		</>
	);
};

export const CreateTreeStateButton = memo(CreateTreeStateButtonComponent);
